import { useEffect, useState } from 'react';
import { api } from '../lib/api';

export type FeatureFlagsMap = Record<string, boolean>;

const DEFAULT: FeatureFlagsMap = {};

let cache: FeatureFlagsMap | null = null;
let inflight: Promise<FeatureFlagsMap> | null = null;
const listeners = new Set<(v: FeatureFlagsMap) => void>();

function emit(v: FeatureFlagsMap): void {
  for (const fn of listeners) fn(v);
}

async function load(): Promise<FeatureFlagsMap> {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = api.getFeatureFlags().then(
    (v) => {
      cache = v;
      inflight = null;
      emit(v);
      return v;
    },
    () => {
      inflight = null;
      return DEFAULT;
    },
  );
  return inflight;
}

export function invalidateFeatureFlagsCache(): void {
  cache = null;
}

// MF-199 — read per-user resolved feature flags. Module-level cache shared
// across components; refreshes on window focus so admin mode flips propagate
// within ~30s without a full page reload (matches the 30s backend cache TTL).
export function useFeatureFlags(): FeatureFlagsMap {
  const [value, setValue] = useState<FeatureFlagsMap>(cache ?? DEFAULT);

  useEffect(() => {
    let cancelled = false;
    load().then((v) => {
      if (!cancelled) setValue(v);
    });

    const listener = (v: FeatureFlagsMap) => {
      if (!cancelled) setValue(v);
    };
    listeners.add(listener);

    const onFocus = () => {
      cache = null;
      load().then((v) => {
        if (!cancelled) setValue(v);
      });
    };
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      listeners.delete(listener);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  return value;
}

// Convenience: boolean read for a single key. Treats an unknown key as
// disabled (matches backend resolver behavior for missing rows).
export function useFeature(key: string): boolean {
  const flags = useFeatureFlags();
  return flags[key] === true;
}
